import { DictionariesService } from "@sa/rep-mdm";
import { MultiplicityType, Parameter } from "@sa/rep-core";
import { OLAPQuery, OLAPService } from "@sa/rep-olap";
import { ServiceProvider } from "@sa/rep-services-provider";
import { IExportQueryBuilder } from "./IExportQueryBuilder";
import { IDataExtractor } from "./IDataExtractor";
import { ServicesMaps } from '../../MapReportingProvider';
import { MapDataCountry } from "../../models/MapDataCountry";
import { MapDataSource } from "../../models/MapDataSource";
import { MapDimension } from "../../models/MapDimension";
import { AggregationType, DataType, SelectionMode } from "@sa/rep-common";
import { groupBy, orderBy, sumBy } from "lodash";
import { Cell } from "@sa/rep-mdde";
import { MapExportQuery } from "../../models/MapExportQuery";

export abstract class DataExtractor implements IDataExtractor, IExportQueryBuilder {
  protected olap: OLAPService;
  protected dictionaries: DictionariesService;
  protected countriesDimension?: MapDimension;
  protected queryPipeline: ((query: OLAPQuery) => OLAPQuery)[] = [];
  protected dataPipeline: ((data: MapDataCountry, cell: Cell) => MapDataCountry)[] = [];

  constructor(serviceProvider: ServiceProvider) {
    this.olap = serviceProvider.resolve(ServicesMaps.OLAP);
    this.dictionaries = serviceProvider.resolve(ServicesMaps.Dictionaries);
  }

  protected validate(dimensions: (MapDimension | null | undefined)[]) {
    if (!dimensions.every(Boolean)) {
      const noValidDimensions = dimensions.filter(e => !e);
      throw new Error(`One of the dimensions not found. (${noValidDimensions.map(e => e?.hierarchy).join(', ')})`);
    }
  }

  async buildExportQuery(dataSource: MapDataSource, dimensions: MapDimension[], values: Record<string, unknown[]>): Promise<MapExportQuery> {
    const query = new MapExportQuery();
    query.title = dataSource.name;
    const updateDate = dataSource.updateDate ? new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'short' }).format(dataSource.updateDate) : '';
    query.source = `${dataSource.name} ${updateDate}`;
    query.dataSourceIdentifier = dataSource.identifier;
    query.parameters = dimensions.map(dim => {
      const value = values[dim.hierarchy]?.flat();
      return {
        allowNullable: true,
        id: `@${dim.hierarchy.split('.')[0]}`,
        multiplicityType: dim.multiplicityType || (!Array.isArray(value) ? MultiplicityType.Single : MultiplicityType.Multiple),
        type: dim.dataType || DataType.Text,
        value
      };
    });
    return query;
  }

  async fetchData(dataSource: MapDataSource, dimensions: MapDimension[], values: Record<string, unknown[]>): Promise<Map<string, MapDataCountry>> {
    const cube = await this.olap.getCube({ id: dataSource.identifier });
    const rows = dimensions.filter(e => e.id !== this.countriesDimension?.id);
    const query: OLAPQuery = this.queryPipeline.reduce((result, pipe) => pipe(result), {
      sourceResolveOptions: {
        sourceId: cube.id,
      },
      columns: {
        showEmpty: false,
        parts: [this.countriesDimension].filter(Boolean).map(dimension => {
          const selection = values[dimension!.hierarchy];
          return {
            path: dimension!.hierarchy,
            selection, selectionMode: SelectionMode.Include,
          }
        }).concat([
          {
            path: "Measures",
            measures: [{ id: "value", aggregationType: AggregationType.Sum }]
          } as any
        ]),
      },
      rows: {
        showEmpty: false,
        parts: rows.map(dimension => {
          const selection = values[dimension.hierarchy];
          return {
            path: dimension.hierarchy,
            selection, selectionMode: SelectionMode.Include,
          }
        })
      }
    } as OLAPQuery);

    const { data } = await cube.query(query as OLAPQuery);
    const allCells = data.rows.flatMap(e => {
      e.cells.forEach(c => Object.assign(c.selection, e.selection));
      return e.cells;
    });
    const groupedCells = groupBy(allCells, e => {
      const s = e.selection;
      const dimensionsKeys = Object.keys(s).filter(e => e !== "Measures");
      return dimensionsKeys.map(e => `${e}:${s[e]}`).join('::');
    });
    const groups = Object.keys(groupedCells).map(e => groupedCells[e]);
    const cells = groups.map(group => {
      const valueCell = group.find(e => e.selection['Measures'] === 'value')!;
      const otherMeasuresCells = group.filter(e => e !== valueCell);
      otherMeasuresCells.forEach(cell => {
        const measureName = cell.selection['Measures'];
        Object.assign(valueCell, { [measureName]: cell.value })
      });
      return valueCell;
    });
    const mapData = new Map<string, MapDataCountry>();
    const countryDim = this.countriesDimension!;
    const countriesDictionary = await this.dictionaries.getDictionary({ id: countryDim.identifier, parameters: {} });
    const countries = await countriesDictionary.getAll();
    const onlyMapCountries = countries.filter(e => e.getValue<string>('code'));
    onlyMapCountries.forEach(country => {
      const id = country.getValue<unknown>('id');
      const code = country.getValue<string>('code')!;
      const nameIsPrimitive = typeof country.getValue<any>('name') === "string";
      const name = nameIsPrimitive ? country.getValue<string>('name') : country.getValue<any>('name')[9];
      const candidates = cells.filter(c => (c.value !== null && c.value !== undefined) && c.selection[countryDim.hierarchy] === id);
      const value = candidates.length ? sumBy(candidates, e => (e.value === null || e.value === undefined) ? 0 : e.value) : undefined;
      const data = this.dataPipeline.reduce((acc, pipe) => (candidates.forEach(cell => pipe(acc, cell)), acc), new MapDataCountry(code, name, value));
      mapData.set(code, data);
    });

    return mapData;
  }
}